<template>
  <canvas></canvas>
</template>

<script>
import timelineCanvas from './mixins/timelineCanvas.js';
import { TimelineSegmentId } from '../../../modules/ScattData.js';
import referencePool from '../../../modules/referencePool.js';
import utils from '../../../modules/utils.js';
import canvasUtils from './modules/canvasUtils.js';
import Color from '../../../modules/Color.js';

const segmentPaddingPx = 2;
const segmentIdWidthPxMinToRenderText = 15;
const segmentIconWidthPxMinToRederText = 15;

const blackColor = new Color(0, 0, 0, 1);

const segmentBackgroundColorReferedAndSelected = new Color(155, 106, 106, 0.5);
const segmentBackgroundColorRefered            = new Color(206, 144, 144, 0.5);
const segmentBackgroundColorSelected           = new Color(134, 192, 215, 0.5);
const segmentBackgroundColor                   = new Color(220, 248, 251, 0.5);

const segmentBorderColorReferedAndSelected = new Color(110, 68, 68, 0.5);
const segmentBorderColorRefered            = new Color(170, 110, 110, 0.5);
const segmentBorderColorSelected           = new Color(100, 160, 200, 0.5);
const segmentBorderColor                   = new Color(190, 210, 220, 0.5);

export default {
  mixins: [
    timelineCanvas,
  ],

  watch: {
    visibleTimelineDataSet: {
      handler() { this.$_setDirty(true); },
      deep: true,
      immediate: true,
    },
    timelineSegmentDomRects() { this.$_setDirty(true); },
    '$data.$_timelineSegmentReferenceCount'() { this.$_setDirty(true); },
    selectedTimelineSegmentIds() { this.$_setDirty(true); },
  },

  props: {
    rulerVerticalOffsetPx:      { type: Number },
    verticalOffsetPx:           { type: Number },
    timelineHeightPx:           { type: Number },
    timeResolution:             { type: Number },
    selectedTimelineSegmentIds: { type: Array },
    visibleTimelineDataSet:     { type: Object },
    visibleTimelineDataIdxToId: { type: Array },
    timelineSegmentDomRects:    { type: Object },
  },

  data() {
    return {
      $_timelineSegmentReferenceCount: {},
      $_onReferencePoolRegisterCallbackId: undefined,
      $_onReferencePoolUnregisterCallbackId: undefined,
    }
  },

  computed: {
    $_timelineSegmentCanvasDomRect() {
      return new DOMRect(
        0,
        this.rulerVerticalOffsetPx,
        this.$data.$_timelineCanvasWidthPx,
        this.$data.$_timelineCanvasHeightPx - this.rulerVerticalOffsetPx
      );
    },
  },

  created() {
    this.$_incrementRefernceCount(undefined, Object.values(referencePool.getAllReferences()));
    this.$data.$_onReferencePoolRegisterCallbackId = referencePool.registerOnRegisterCallback(this.$_incrementRefernceCount);
    this.$data.$_onReferencePoolUnregisterCallbackId = referencePool.registerOnUnregisterCallback(this.$_decrementReferenceCount);
  },

  destroyed() {
    referencePool.unregisterOnRegisterCallback(this.$data.$_onReferencePoolRegisterCallbackId);
    referencePool.unregisterOnUnregisterCallback(this.$data.$_onReferencePoolUnregisterCallbackId);
  },

  methods: {
    /* private */
    $_getTimelineSegmentDomRect(timelineDataId, segmentId) {
      if (!Object.keys(this.timelineSegmentDomRects).includes(timelineDataId)) return undefined;
      if (!Object.keys(this.timelineSegmentDomRects[timelineDataId]).includes(segmentId)) return undefined;
      return this.timelineSegmentDomRects[timelineDataId][segmentId];
    },

    /* public */
    getOffsetForRectTimelineSegmentToBeInCanvas(timelineSegmentId) {
      let timelineDataId = timelineSegmentId.timelineDataId;
      let segmentId = timelineSegmentId.segmentId;
      let timelineSegmentDomRect = this.$_getTimelineSegmentDomRect(timelineDataId, segmentId);
      if (timelineSegmentDomRect !== undefined) {
        return utils.getOffsetRectToBeInRect(timelineSegmentDomRect, this.$_timelineSegmentCanvasDomRect);
      } else {
        return undefined;
      }
    },

    draw() {
      this.$_draw(() => {
        drawSegments.call(this);
        clearRulerArea.call(this);
      });

      function drawSegments() {
        for(let visibleTimelineDataId of this.visibleTimelineDataIdxToId) {
          let visibleTimelineData = this.visibleTimelineDataSet[visibleTimelineDataId];
          for (let [ segmentId, segment ] of Object.entries(visibleTimelineData.segments)) {
            let isLocked = segment.locked || visibleTimelineData.locked || visibleTimelineData.readonly;
            let timelineSegmentDomRect = this.$_getTimelineSegmentDomRect(visibleTimelineDataId, segmentId);
            if (timelineSegmentDomRect !== undefined) {
              if (utils.areRectsOverwrapped(this.$_timelineSegmentCanvasDomRect, timelineSegmentDomRect)) {
                drawTimelineSegment.call(this, timelineSegmentDomRect,
                  isTimelineSegmentRefered.call(this, visibleTimelineDataId, segmentId),
                  isTimelineSegmentSelected.call(this, visibleTimelineDataId, segmentId));
                drawContentsOnTimelineSegment.call(this, timelineSegmentDomRect, segmentId, segment.data, isLocked);
              }
            }
          }
        }

        function drawTimelineSegment(timelineSegmentDomRect, isRefered, isSelected) {
          canvasUtils.drawRect(
            this.$data.$_timelineCanvasContext,
            timelineSegmentDomRect,
            {
              stroke: true,
              strokeStyle: getRgbColorOfSegmentBorder(isRefered, isSelected).styleString,
              fill: true,
              fillStyle: getRgbColorOfSegmentBackground(isRefered, isSelected).styleString,
            },
          );

          function getRgbColorOfSegmentBackground(isRefered, isSelected) {
            if (isRefered) {
              if (isSelected) {
                return segmentBackgroundColorReferedAndSelected;
              } else {
                return segmentBackgroundColorRefered;
              }
            } else {
              if (isSelected) {
                return segmentBackgroundColorSelected;
              } else {
                return segmentBackgroundColor;
              }
            }
          }

          function getRgbColorOfSegmentBorder(isRefered, isSelected) {
            if (isRefered) {
              if (isSelected) {
                return segmentBorderColorReferedAndSelected;
              } else {
                return segmentBorderColorRefered;
              }
            } else {
              if (isSelected) {
                return segmentBorderColorSelected;
              } else {
                return segmentBorderColor;
              }
            }
          }
        }

        function drawContentsOnTimelineSegment(timelineSegmentDomRect, segmentId, data, isLocked) {
          {
            let segmentIdFontSizePx = 8;
            let segmentIdLeftX = timelineSegmentDomRect.x + segmentPaddingPx;
            let segmentIdTopY = timelineSegmentDomRect.y + segmentPaddingPx;
            let segmentIdWidthPxMax = timelineSegmentDomRect.x + timelineSegmentDomRect.width - segmentIdLeftX;
            let iconFontSizePx = 8;
            let iconLeftX = timelineSegmentDomRect.x + segmentPaddingPx;
            let iconBottomY = timelineSegmentDomRect.y + timelineSegmentDomRect.height - segmentPaddingPx;
            let iconWidthPxMax = timelineSegmentDomRect.x + timelineSegmentDomRect.width - iconLeftX;
            let timelineSegmentCanvas = this.$data.$_timelineCanvasContext;
            timelineSegmentCanvas.fillStyle = blackColor.styleString;
            timelineSegmentCanvas.textAlign = 'left';
            timelineSegmentCanvas.textBaseline = 'top';
            if (segmentIdWidthPxMax > segmentIdWidthPxMinToRenderText) {
              timelineSegmentCanvas.font = `normal ${segmentIdFontSizePx}px sans-serif`;
              timelineSegmentCanvas.fillText(`[${segmentId}]`, segmentIdLeftX, segmentIdTopY, segmentIdWidthPxMax);
            }
            timelineSegmentCanvas.textBaseline = 'bottom';
            if (isLocked && (iconWidthPxMax > segmentIconWidthPxMinToRederText)) {
              timelineSegmentCanvas.font = `normal ${iconFontSizePx}px material-design-icons`;
              timelineSegmentCanvas.fillText('lock', iconLeftX, iconBottomY, iconWidthPxMax);
            }
            data.drawOnTimelineSegment({
              canvas2d: timelineSegmentCanvas,
              rect: timelineSegmentDomRect,
              paddingPx: segmentPaddingPx,
              timeResolution: this.timeResolution,
            });
          }
        }

        function isTimelineSegmentRefered(timelineDataIdRefered, segmentIdRefered) {
          if (!Object.keys(this.$data.$_timelineSegmentReferenceCount).includes(timelineDataIdRefered)) {
            return false;
          }
          if (!Object.keys(this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered]).includes(segmentIdRefered)) {
            return false;
          }
          return true;
        }

        function isTimelineSegmentSelected(timelineDataIdRefered, segmentIdRefered) {
          let timelineSegmentId = new TimelineSegmentId(timelineDataIdRefered, segmentIdRefered);
          for (let selectedTimelineSegmentId of this.selectedTimelineSegmentIds) {
            if (selectedTimelineSegmentId.isSameAs(timelineSegmentId)) {
              return true;
            }
          }
          return false;
        }
      }

      function clearRulerArea() {
        let timelineSegmentCanvas = this.$data.$_timelineCanvasContext;
        timelineSegmentCanvas.clearRect(
          0,
          0,
          this.$data.$_timelineCanvasWidthPx,
          this.rulerVerticalOffsetPx,
        );
      }
    },

    $_incrementRefernceCount(_, registeredReferences) {
      let numRegisteredReferences = registeredReferences.length;
      for (let registeredReferenceIdx = 0; registeredReferenceIdx < numRegisteredReferences; ++registeredReferenceIdx) {
        let registeredReference = registeredReferences[registeredReferenceIdx];
        let timelineDataIdRefered = registeredReference.referTo.timelineDataId;
        let segmentIdRefered = registeredReference.referTo.segmentId;
        if (!Object.keys(this.$data.$_timelineSegmentReferenceCount).includes(timelineDataIdRefered)) {
          this.$set(this.$data.$_timelineSegmentReferenceCount, timelineDataIdRefered, {});
        }
        if (!Object.keys(this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered]).includes(segmentIdRefered)) {
          this.$set(this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered], segmentIdRefered, 0);
        }
        ++this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered][segmentIdRefered];
      }
      this.$_setDirty(true);
    },

    $_decrementReferenceCount(_, unregisteredReferences) {
      let numUnregisteredReferences = unregisteredReferences.length;
      for (let unregisteredReferenceIdx = 0; unregisteredReferenceIdx < numUnregisteredReferences; ++unregisteredReferenceIdx) {
        let unregisteredReference = unregisteredReferences[unregisteredReferenceIdx];
        let timelineDataIdRefered = unregisteredReference.referTo.timelineDataId;
        let segmentIdRefered = unregisteredReference.referTo.segmentId;
        --this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered][segmentIdRefered];
        if (this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered][segmentIdRefered] === 0) {
          this.$delete(this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered], segmentIdRefered);
        }
        if (Object.keys(this.$data.$_timelineSegmentReferenceCount[timelineDataIdRefered]).length === 0) {
          this.$delete(this.$data.$_timelineSegmentReferenceCount, timelineDataIdRefered);
        }
      }
      this.$_setDirty(true);
    },
  }
};
</script>
